import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import PrintErrorMessage from '@/components/organisms/feedback/PrintErrorMessage';

const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
// Radix Select n'accepte pas de valeur vide : sentinelle pour "non renseigné".
const NONE_VALUE = 'NONE';

const ProcessOverflowInterestsDialog = ({ open, onOpenChange, years = [], defaultYear, onConfirm, loading, error }) => {
    const [t] = useTranslation('global');
    const [year, setYear] = useState(NONE_VALUE);
    const [month, setMonth] = useState(NONE_VALUE);

    useEffect(() => {
        if (open) {
            setYear(defaultYear ? String(defaultYear) : NONE_VALUE);
            setMonth(NONE_VALUE);
        }
    }, [open, defaultYear]);

    const sortedYears = [...years].sort((a, b) => b - a);
    const hasYear = year !== NONE_VALUE;

    const handleOpenChange = (nextOpen) => {
        if (loading) return;
        onOpenChange(nextOpen);
    };

    const handleYearChange = (value) => {
        setYear(value);
        if (value === NONE_VALUE) setMonth(NONE_VALUE);
    };

    const handleConfirm = () => {
        onConfirm(
            hasYear ? Number(year) : null,
            hasYear && month !== NONE_VALUE ? Number(month) : null,
        );
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        <Label message="consultants.statement.overflowInterests.dialogTitle" size="h4" />
                    </DialogTitle>
                    <DialogDescription className="flex flex-col gap-4 py-4">
                        <Label message="consultants.statement.overflowInterests.dialogDescription" />

                        <div className="grid grid-cols-2 gap-secondDegree">
                            <div className="flex flex-col gap-2">
                                <Label fontWeight="semibold" message="consultants.statement.overflowInterests.yearLabel" />
                                <Select value={year} onValueChange={handleYearChange} disabled={loading}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NONE_VALUE}>
                                            {t('consultants.statement.overflowInterests.noYear')}
                                        </SelectItem>
                                        {sortedYears.map((item) => (
                                            <SelectItem key={item} value={String(item)}>{item}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label fontWeight="semibold" message="consultants.statement.overflowInterests.monthLabel" />
                                <Select value={month} onValueChange={setMonth} disabled={loading || !hasYear}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NONE_VALUE}>
                                            {t('consultants.statement.overflowInterests.noMonth')}
                                        </SelectItem>
                                        {MONTH_KEYS.map((monthKey, index) => (
                                            <SelectItem key={monthKey} value={String(index + 1)}>
                                                {t(`global.months.${monthKey}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {error && <PrintErrorMessage error={error} />}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="sm:justify-start">
                    <Button
                        type="button"
                        style="ghost"
                        text="global.actions.cancel"
                        onClick={() => handleOpenChange(false)}
                        disabled={loading}
                    />
                    <div className="flex items-center justify-center">
                        {loading && <span className="loader" />}
                    </div>
                    <Button
                        style="fill"
                        text="consultants.statement.overflowInterests.confirm"
                        className="w-full"
                        onClick={handleConfirm}
                        disabled={loading}
                    />
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ProcessOverflowInterestsDialog;
